"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  quote: "The expedition to the Patagonian peaks was more than a trip; it was a spiritual awakening. VOYAGE handled every detail with such grace that I could simply focus on the horizon.",
  name: "Elena Rossi",
  role: "Patagonia Explorer",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/traveler_elena.png",
  brokerageLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/badge_adventure_1.png"
}, {
  id: 2,
  quote: "I've traveled the world for decades, but the curated silence of the Sahara trek was something I've never experienced. Their cinematic eye for detail is present in every moment.",
  name: "Julian Vance",
  role: "Sahara Trekker",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/traveler_julian.png",
  brokerageLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/badge_adventure_2.png"
}, {
  id: 3,
  quote: "From the hidden temples of Kyoto to the misty fjords of Norway, VOYAGE finds the soul of a place. The itinerary felt like a personal letter written just for my curiosity.",
  name: "Aiko Tanaka",
  role: "Cultural Voyager",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/traveler_aiko.png",
  brokerageLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/badge_adventure_3.png"
}, {
  id: 4,
  quote: "The level of luxury combined with raw, authentic adventure is a rare find. I returned home with a full heart and a perspective that has been permanently shifted.",
  name: "Marcus Thorne",
  role: "Alpine Climber",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/traveler_marcus.png",
  brokerageLogo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650028/img/badge_adventure_4.png"
}];
export default function VoiceOfTheAgent() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223125" id="voices-from-the-trail" className="relative w-full overflow-hidden bg-background py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <Quote className="absolute -top-10 -left-10 h-96 w-96 -rotate-12 text-primary/5" aria-hidden="true" />
        <Quote className="absolute -bottom-20 -right-10 h-[30rem] w-[30rem] rotate-12 text-primary/5" aria-hidden="true" />
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div ref={headerRef} className={`mb-16 flex flex-col items-center text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-default text-4xl font-bold uppercase tracking-widest text-foreground md:text-5xl lg:text-6xl">
            Voices From <span className="text-primary">The Trail</span>
          </h2>
          <p className="mt-4 max-w-2xl font-sans text-lg text-muted-foreground">
            The most profound stories aren't told by us, but by those who have walked the path. Discover the impact of a journey well-traveled.
          </p>
        </div>

        <div ref={carouselRef} className={`relative transition-all duration-1000 delay-200 ${isCarouselVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:basis-1/2 md:pl-8 lg:basis-1/2">
                  <Card className="flex h-full flex-col justify-between border border-border/40 bg-card p-8 shadow-xl transition-transform duration-500 hover:-translate-y-2 md:p-10 rounded-xl">
                    <CardContent className="p-0">
                      <Quote className="mb-6 h-10 w-10 text-primary/30" />
                      <p className="font-serif text-xl italic leading-relaxed text-foreground md:text-2xl">
                        "{testimonial.quote}"
                      </p>
                    </CardContent>
                    
                    <div className="mt-10 flex items-center justify-between border-t border-border/30 pt-6">
                      <div className="flex items-center gap-4">
                        <SafeHydrate><Avatar className="h-14 w-14 border border-primary/20">
                          <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                          <AvatarFallback className="font-default text-lg font-bold bg-accent text-accent-foreground">
                            {testimonial.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <h4 className="font-default text-lg font-bold text-foreground">
                            {testimonial.name}
                          </h4>
                          <p className="font-sans text-xs uppercase tracking-widest text-primary">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                      <div className="hidden shrink-0 sm:block">
                        <img src={testimonial.brokerageLogo} alt="Expedition Badge" className="h-10 w-auto object-contain opacity-40 grayscale transition-all duration-500 hover:opacity-100 hover:grayscale-0" />
                      </div>
                    </div>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            
            <div className="mt-12 flex items-center justify-center gap-4 md:absolute md:top-1/2 md:mt-0 md:w-full md:-translate-y-1/2 md:justify-between md:px-4">
              <CarouselPrevious className="relative left-0 top-0 translate-y-0 border-border/50 bg-background text-foreground shadow-sm hover:bg-primary hover:text-primary-foreground md:absolute md:-left-16 h-12 w-12 rounded-full transition-all duration-300" />
              <CarouselNext className="relative right-0 top-0 translate-y-0 border-border/50 bg-background text-foreground shadow-sm hover:bg-primary hover:text-primary-foreground md:absolute md:-right-16 h-12 w-12 rounded-full transition-all duration-300" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}